import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { lessonsAPI, athletesAPI, venuesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Calendar as CalendarIcon, Clock, Users, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { SportSelector } from '../components/SportTypes';

const ITEMS_PER_PAGE = 10;

export default function LessonsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isPro = user?.subscription_plan === 'pro';
  const [allLessons, setAllLessons] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');
  const emptyFormData = useMemo(() => ({
    venue_id: '',
    date: '',
    start_time: '',
    duration_minutes: '',
    athlete_ids: [],
    sport_type: '',
    pricing_type: 'standard',
    fixed_coach_fee: '',
    extra_athletes: '',
    description: '',
  }), []);
  const [formData, setFormData] = useState(emptyFormData);

  useEffect(() => {
    fetchData();
  }, []);

  // Aggiorna il filtro quando cambia la URL
  useEffect(() => {
    const urlFilter = searchParams.get('filter') || 'all';
    setFilter(urlFilter);
    setCurrentPage(1);
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setEditingLesson(null);
      setFormData(emptyFormData);
      setDialogOpen(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('create');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, emptyFormData]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || allLessons.length === 0) return;

    const lessonToEdit = allLessons.find((lesson) => String(lesson.id) === String(editId));
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });

    if (!lessonToEdit) {
      toast.error('Lezione non trovata');
      return;
    }

    setEditingLesson(lessonToEdit);
    setFormData({
      venue_id: lessonToEdit.venue_id,
      date: lessonToEdit.date,
      start_time: lessonToEdit.start_time,
      duration_minutes: lessonToEdit.duration_minutes.toString(),
      athlete_ids: lessonToEdit.athlete_ids,
      sport_type: lessonToEdit.sport_type || '',
      pricing_type: lessonToEdit.pricing_type || 'standard',
      fixed_coach_fee: lessonToEdit.fixed_coach_fee || '',
      extra_athletes: lessonToEdit.extra_athletes?.toString() || '',
      description: lessonToEdit.description || '',
    });
    setDialogOpen(true);
  }, [searchParams, setSearchParams, allLessons, emptyFormData]);

  const fetchData = async () => {
    try {
      const [lessonsRes, athletesRes, venuesRes] = await Promise.all([
        lessonsAPI.getAll(),
        athletesAPI.getAll(),
        venuesAPI.getAll(),
      ]);
      // Ordina lezioni dalla più recente alla meno recente
      const sortedLessons = lessonsRes.data.sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.start_time}`);
        const dateB = new Date(`${b.date}T${b.start_time}`);
        return dateB - dateA; // Più recente prima
      });
      setAllLessons(sortedLessons);
      // Ordina atleti alfabeticamente per la selezione
      const sortedAthletes = athletesRes.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
      );
      setAthletes(sortedAthletes);
      setVenues(venuesRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  // Filtra le lezioni in base al filtro selezionato
  const getFilteredLessons = () => {
    const today = new Date().toISOString().split('T')[0];
    if (filter === 'upcoming') {
      return allLessons.filter(lesson => lesson.date >= today);
    } else if (filter === 'past') {
      return allLessons.filter(lesson => lesson.date < today);
    }
    return allLessons;
  };

  const lessons = getFilteredLessons();

  // Paginazione
  const totalPages = Math.ceil(lessons.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLessons = lessons.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
    const nextParams = new URLSearchParams(searchParams);
    if (newFilter === 'all') {
      nextParams.delete('filter');
    } else {
      nextParams.set('filter', newFilter);
    }
    setSearchParams(nextParams);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.venue_id) {
      toast.error('Seleziona una struttura');
      return;
    }
    if (formData.athlete_ids.length === 0) {
      toast.error('Seleziona almeno un atleta');
      return;
    }
    const durationValue = parseInt(formData.duration_minutes, 10);
    if (!durationValue) {
      toast.error('Inserisci la durata');
      return;
    }
    if (Number.isNaN(durationValue) || durationValue <= 0 || durationValue % 5 !== 0) {
      toast.error('La durata deve essere un multiplo di 5 minuti');
      return;
    }
    
    try {
      const data = {
        ...formData,
        duration_minutes: durationValue,
        extra_athletes: parseInt(formData.extra_athletes) || 0,
      };

      const returnTo = searchParams.get('returnTo');
      if (editingLesson) {
        await lessonsAPI.update(editingLesson.id, data);
        toast.success('Lezione aggiornata!');
      } else {
        await lessonsAPI.create(data);
        toast.success('Lezione creata!');
      }
      setDialogOpen(false);
      setFormData(emptyFormData);
      setEditingLesson(null);
      fetchData();
      if (editingLesson && returnTo) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('returnTo');
        setSearchParams(nextParams, { replace: true });
        navigate(returnTo);
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({
      venue_id: lesson.venue_id,
      date: lesson.date,
      start_time: lesson.start_time,
      duration_minutes: lesson.duration_minutes.toString(),
      athlete_ids: lesson.athlete_ids,
      sport_type: lesson.sport_type || '',
      pricing_type: lesson.pricing_type || 'standard',
      fixed_coach_fee: lesson.fixed_coach_fee || '',
      extra_athletes: lesson.extra_athletes?.toString() || '',
      description: lesson.description || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa lezione?')) return;

    try {
      await lessonsAPI.delete(id);
      toast.success('Lezione eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const toggleAthlete = (athleteId) => {
    setFormData((prev) => ({
      ...prev,
      athlete_ids: prev.athlete_ids.includes(athleteId)
        ? prev.athlete_ids.filter((id) => id !== athleteId)
        : [...prev.athlete_ids, athleteId],
    }));
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div data-testid="lessons-page" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">Lezioni</h1>
            <p className="text-zinc-400 text-sm sm:text-base">
              {filter === 'upcoming' ? 'Lezioni prossime' : filter === 'past' ? 'Lezioni passate' : 'Tutte le lezioni'} ({lessons.length})
            </p>
          </div>

          <Button
            onClick={() => {
              setEditingLesson(null);
              setFormData(emptyFormData);
              setDialogOpen(true);
            }}
            className="hidden md:flex bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl h-10"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Lezione
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent 
              onInteractOutside={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
              className="bg-zinc-900 border border-white/10 rounded-xl w-[calc(100vw-24px)] max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingLesson ? 'Modifica Lezione' : 'Nuova Lezione'}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingLesson ? 'Modifica i dettagli della lezione' : 'Compila i dettagli per creare una nuova lezione'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome Lezione */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Nome Lezione</Label>
                  <SportSelector 
                    sportType={formData.sport_type}
                    suggestions={[...new Set(allLessons.map((lesson) => (lesson.sport_type || '').trim()).filter(Boolean))]}
                    onTypeChange={(value) => setFormData({ ...formData, sport_type: value })}
                  />
                </div>

                {/* Data */}
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-zinc-300 text-sm">Data</Label>
                  <Input
                    id="date"
                    data-testid="lesson-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.date ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
                    required
                  />
                </div>

                {/* Orario */}
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-zinc-300 text-sm">Ora</Label>
                  <Input
                    id="time"
                    data-testid="lesson-time-input"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.start_time ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
                    required
                  />
                </div>

                {/* Durata */}
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-zinc-300 text-sm">Durata (minuti)</Label>
                  <Input
                    id="duration"
                    data-testid="lesson-duration-input"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    placeholder="60"
                    className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.duration_minutes ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
                    min="5"
                    step="5"
                    required
                  />
                </div>

                {/* Struttura */}
                <div className="space-y-2">
                  <Label htmlFor="venue" className="text-zinc-300 text-sm">Struttura</Label>
                  <Select value={formData.venue_id} onValueChange={(value) => setFormData({ ...formData, venue_id: value })} required>
                    <SelectTrigger data-testid="lesson-venue-select" className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.venue_id ? 'border-[#ccff00] text-[#ccff00]' : ''}`}>
                      <SelectValue placeholder="Seleziona struttura" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                      {venues.map((venue) => (
                        <SelectItem key={venue.id} value={venue.id} className="cursor-pointer hover:bg-zinc-800">
                          {venue.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Atleti Presenti */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Atleti Presenti ({formData.athlete_ids.length} selezionati)</Label>
                  <div className={`max-h-40 overflow-y-auto space-y-2 p-3 bg-zinc-950 rounded-xl border transition-colors ${formData.athlete_ids.length > 0 ? 'border-[#ccff00]' : 'border-zinc-800'}`}>
                    {athletes.length === 0 ? (
                      <p className="text-yellow-500 text-xs">Nessun atleta disponibile. Inseriscine uno prima.</p>
                    ) : (
                      [...athletes].sort((a, b) => a.name.localeCompare(b.name)).map((athlete) => (
                        <div key={athlete.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`athlete-${athlete.id}`}
                            checked={formData.athlete_ids.includes(athlete.id)}
                            onCheckedChange={() => toggleAthlete(athlete.id)}
                            className="border-zinc-600 data-[state=checked]:bg-[#ccff00] data-[state=checked]:border-[#ccff00]"
                          />
                          <label htmlFor={`athlete-${athlete.id}`} className={`text-sm cursor-pointer ${formData.athlete_ids.includes(athlete.id) ? 'text-[#ccff00]' : 'text-white'}`}>
                            {athlete.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Campo Atleti Extra */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label className="text-zinc-300 text-sm">Eventuali atleti extra</Label>
                    <button 
                      type="button" 
                      onClick={() => toast.info('Inserisci il numero di altri atleti presenti in contemporanea con cui condividere il costo della struttura.')}
                      className="text-zinc-400 hover:text-[#ccff00] transition-colors"
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <Input
                    type="number"
                    value={formData.extra_athletes}
                    onChange={(e) => setFormData({...formData, extra_athletes: e.target.value})}
                    placeholder="0"
                    className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.extra_athletes && parseInt(formData.extra_athletes) > 0 ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
                    min="0"
                  />
                </div>

                {/* Selezione Tipo Tariffa */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">Compenso allenatore</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, pricing_type: 'standard', fixed_coach_fee: ''})}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                        formData.pricing_type === 'standard' 
                          ? 'bg-[#ccff00] text-black' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      Standard
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, pricing_type: 'fixed'})}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                        formData.pricing_type === 'fixed' 
                          ? 'bg-[#ccff00] text-black' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      Forfettario
                    </button>
                  </div>
                  {formData.pricing_type === 'standard' && (
                    <p className="text-zinc-500 text-xs">Verrà applicato il compenso orario impostato nel tuo profilo</p>
                  )}
                </div>

                {/* Campo Costo Forfettario (solo se selezionato) */}
                {formData.pricing_type === 'fixed' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300 text-sm">Compenso Totale Allenatore (€)</Label>
                    <Input
                      type="number"
                      value={formData.fixed_coach_fee}
                      onChange={(e) => setFormData({...formData, fixed_coach_fee: e.target.value})}
                      placeholder="Es. 80"
                      className={`w-full bg-zinc-950 border-zinc-800 rounded-xl h-11 transition-colors ${formData.fixed_coach_fee ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
                      min="0"
                      step="0.01"
                      required
                    />
                    <p className="text-zinc-500 text-xs">Questo importo verrà diviso equamente tra gli atleti</p>
                  </div>
                )}

                {/* Descrizione — solo Pro */}
                {isPro ? (
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-zinc-300 text-sm">
                      Descrizione <span className="text-zinc-500">(facoltativo)</span>
                    </Label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Abbiamo lavorato su..."
                      rows={3}
                      className={`w-full bg-zinc-950 border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 resize-none focus:outline-none focus:ring-1 focus:ring-[#ccff00] transition-colors ${formData.description ? 'border-[#ccff00]' : 'border-zinc-800'}`}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3">
                    <span className="text-zinc-500 text-sm">Descrizione lezione</span>
                    <span className="text-[10px] font-black text-black bg-[#ccff00] rounded-md px-1.5 py-0.5 tracking-wide">PRO</span>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    data-testid="save-lesson-btn"
                    type="submit"
                    className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                  >
                    {editingLesson ? 'Aggiorna' : 'Inserisci Lezione'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filtri */}
        <div className="flex gap-2 mb-4">
          <Button
            onClick={() => handleFilterChange('all')}
            variant={filter === 'all' ? 'default' : 'outline'}
            className={`rounded-xl text-sm ${filter === 'all' ? 'bg-[#ccff00] text-black hover:bg-[#b3e600]' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}
          >
            Tutte
          </Button>
          <Button
            onClick={() => handleFilterChange('upcoming')}
            variant={filter === 'upcoming' ? 'default' : 'outline'}
            className={`rounded-xl text-sm ${filter === 'upcoming' ? 'bg-[#ccff00] text-black hover:bg-[#b3e600]' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}
          >
            Prossime
          </Button>
          <Button
            onClick={() => handleFilterChange('past')}
            variant={filter === 'past' ? 'default' : 'outline'}
            className={`rounded-xl text-sm ${filter === 'past' ? 'bg-[#ccff00] text-black hover:bg-[#b3e600]' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}
          >
            Passate
          </Button>
        </div>

        {lessons.length === 0 ? (
          <div className="glass-card p-8 sm:p-12 rounded-xl border border-white/10 text-center">
            <p className="text-zinc-400 text-sm sm:text-lg">
              {filter === 'upcoming' ? 'Nessuna lezione prossima.' : filter === 'past' ? 'Nessuna lezione passata.' : 'Nessuna lezione trovata. Crea la tua prima lezione!'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {paginatedLessons.map((lesson) => {
                const venue = venues.find((v) => v.id === lesson.venue_id);
                return (
                  <div
                    key={lesson.id}
                    data-testid="lesson-card"
                    className="glass-card p-3 rounded-xl border border-white/10 hover:border-[#ccff00]/30 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/lessons/${lesson.id}`)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5">
                          <h3 className="text-base font-bold text-white group-hover:text-[#ccff00]">
                            {lesson.sport_type || 'Generale'}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-[#ccff00]">
                            <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="font-bold">
                              {new Date(lesson.date).toLocaleDateString('it-IT', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                              })}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-white">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="font-medium">{lesson.start_time}</span>
                            <span className="text-zinc-400">({lesson.duration_minutes} min)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-0.5 text-xs text-zinc-400">
                          <span>Struttura: <span className="text-white">{venue?.name || 'N/A'}</span></span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {lesson.athlete_ids.length} {lesson.athlete_ids.length === 1 ? 'atleta' : 'atleti'}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-row gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Button
                          data-testid="edit-lesson-btn"
                          onClick={() => handleEdit(lesson)}
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-[#ccff00] h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid="delete-lesson-btn"
                          onClick={() => handleDelete(lesson.id)}
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-red-500 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 sm:space-x-4 mt-6 sm:mt-8">
                <Button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                >
                  <ChevronLeft className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Indietro</span>
                </Button>
                <span className="text-zinc-400 text-xs sm:text-sm">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-50 rounded-xl text-xs sm:text-sm px-2 sm:px-4"
                >
                  <span className="hidden sm:inline">Avanti</span>
                  <ChevronRight className="w-4 h-4 sm:ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </CoachLayout>
  );
}
